import { eventBus } from './eventBus';
import { logger } from './logger';
import { sessionManager } from './session';

export interface HttpClientConfig {
  baseUrl: string;
  clientKey: string;
  timeout?: number;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean | undefined>;
  skipAuth?: boolean;
  retries?: number;
}

export class HttpClientError extends Error {
  constructor(
    public statusCode: number,
    public code: string,
    message: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'HttpClientError';
  }
}

// Request deduplication cache
const pendingRequests = new Map<string, Promise<unknown>>();

export class HttpClient {
  private baseUrl: string;
  private clientKey: string;
  private timeout: number;

  constructor(config: HttpClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, '');
    this.clientKey = config.clientKey;
    this.timeout = config.timeout || 30000;
  }

  private buildUrl(endpoint: string, params?: RequestOptions['params']): string {
    const url = new URL(endpoint, this.baseUrl);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }
    
    return url.toString();
  }

  private getHeaders(customHeaders?: Record<string, string>, skipAuth?: boolean): Headers {
    const headers = new Headers({
      'Content-Type': 'application/json',
      ...customHeaders,
    });

    if (!skipAuth) {
      headers.set('X-Client-Key', this.clientKey);
      headers.set('X-Session-ID', sessionManager.getSessionId());
    }

    return headers;
  }

  private async executeRequest<T>(
    url: string,
    options: RequestInit,
    method: string
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    eventBus.emit('tm:api:request', { method, url });
    logger.debug(`API Request: ${method} ${url}`);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      eventBus.emit('tm:api:response', { method, url, status: response.status });
      logger.debug(`API Response: ${method} ${url} - ${response.status}`);

      if (!response.ok) {
        let errorBody: Record<string, unknown> = {};
        try {
          errorBody = await response.json();
        } catch {
          // Response body is not JSON
        }

        const error = new HttpClientError(
          response.status,
          this.getErrorCode(response.status),
          (errorBody.detail as string) || (errorBody.message as string) || response.statusText,
          errorBody
        );

        throw error;
      }

      // Handle empty responses
      const text = await response.text();
      if (!text) {
        return {} as T;
      }

      return JSON.parse(text) as T;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof HttpClientError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new HttpClientError(408, 'TIMEOUT', 'Request timed out');
        }
        throw new HttpClientError(0, 'NETWORK_ERROR', error.message);
      }

      throw new HttpClientError(0, 'UNKNOWN_ERROR', 'An unknown error occurred');
    }
  }

  private getErrorCode(status: number): string {
    switch (status) {
      case 400: return 'BAD_REQUEST';
      case 401: return 'UNAUTHORIZED';
      case 403: return 'FORBIDDEN';
      case 404: return 'NOT_FOUND';
      case 409: return 'CONFLICT';
      case 422: return 'VALIDATION_ERROR';
      case 429: return 'RATE_LIMITED';
      case 500: return 'SERVER_ERROR';
      default: return 'UNKNOWN_ERROR';
    }
  }

  private async withRetry<T>(
    fn: () => Promise<T>,
    retries: number,
    delay: number = 1000
  ): Promise<T> {
    try {
      return await fn();
    } catch (error) {
      if (retries <= 0) {
        throw error;
      }

      // Only retry on server errors or network issues
      if (error instanceof HttpClientError) {
        if (error.statusCode >= 500 || error.statusCode === 0) {
          logger.warn(`Retrying request, ${retries} attempts remaining`);
          await new Promise((resolve) => setTimeout(resolve, delay));
          return this.withRetry(fn, retries - 1, delay * 2);
        }
      }

      throw error;
    }
  }

  private getRequestKey(method: string, url: string, body?: unknown): string {
    return `${method}:${url}:${JSON.stringify(body || '')}`;
  }

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const {
      method = 'GET',
      body,
      headers,
      params,
      skipAuth,
      retries = 0,
    } = options;

    const url = this.buildUrl(endpoint, params);
    const requestKey = this.getRequestKey(method, url, body);

    // Deduplicate identical concurrent requests
    if (method === 'GET' && pendingRequests.has(requestKey)) {
      logger.debug(`Deduplicating request: ${requestKey}`);
      return pendingRequests.get(requestKey) as Promise<T>;
    }

    const requestOptions: RequestInit = {
      method,
      headers: this.getHeaders(headers, skipAuth),
    };

    if (body && method !== 'GET') {
      requestOptions.body = JSON.stringify(body);
    }

    const requestPromise = this.withRetry(
      () => this.executeRequest<T>(url, requestOptions, method),
      retries
    );

    if (method === 'GET') {
      pendingRequests.set(requestKey, requestPromise);
      requestPromise.finally(() => {
        pendingRequests.delete(requestKey);
      });
    }

    return requestPromise;
  }

  async get<T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  async put<T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body });
  }

  async delete<T>(endpoint: string, options?: Omit<RequestOptions, 'method'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

let httpClientInstance: HttpClient | null = null;

export function initHttpClient(config: HttpClientConfig): HttpClient {
  httpClientInstance = new HttpClient(config);
  return httpClientInstance;
}

export function getHttpClient(): HttpClient {
  if (!httpClientInstance) {
    throw new Error('HTTP client not initialized. Call initHttpClient first.');
  }
  return httpClientInstance;
}

